import { Color3 } from '@babylonjs/core/Maths/math';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { eJoinType } from '@kb3d/models/classes/enums';
import { JoinGeometryFeature } from '@kb3d/models/classes/features/join-geometry-feature';
import { CustomMeshNode } from '@kb3d/models/classes/meshes/custom-mesh-node';
import { MeshNode } from '@kb3d/models/classes/meshes/mesh-node';
import { SpaceNode } from '@kb3d/models/classes/meshes/space-node';
import { Token } from '@kb3d/models/classes/token';
import { ITrackedClass } from '@kb3d/models/interfaces/tracked-class';
import { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';
import { createJoinGeometryVisualization } from '@kb3d/view/helpers/join-geometry-helper';
import { Subscription } from 'rxjs';
import { FeatureRenderer, SubmeshVertexDataMap } from './feature';
import {
    JoinGeometryDependencyData,
    JoinGeometryGeometryProcessor,
    JoinGeometryWorkerPayload,
} from './geometry-processor/join-geometry-geometry';

const joinedMeshes = new Map<string, Mesh>();

export class JoinGeometryRenderer extends FeatureRenderer<JoinGeometryFeature, JoinGeometryWorkerPayload> {
    modelToken = Token.JoinGeometryFeature;
    bbSubscriptions = new Map<string, Subscription>();

    targetMeshVisualizationMap = new Map<string, Mesh>();
    parentMeshVisualizationMap = new Map<string, Mesh>();
    previewLayer: UtilityLayerRenderer = this.viewer.utilityLayer;
    processor = new JoinGeometryGeometryProcessor(this.scene);

    async makeWorkerPayload(feature: JoinGeometryFeature, geometryData: SubmeshVertexDataMap) {
        const payload = await super.makeWorkerPayload(feature, geometryData);
        const targetMeshNode = feature._manager?.getById<SpaceNode>(feature.meshId);
        if (!targetMeshNode) {
            return payload;
        }
        const targetMesh = this.viewer.getRendererNode(targetMeshNode);
        const thisMesh = this.viewer.getRendererNode(feature._parent!);
        const otherMeshVertexData = geometryData.get(targetMeshNode._dynamicId);
        if (!targetMesh || !thisMesh || !otherMeshVertexData || !(targetMesh instanceof Mesh)) {
            return payload;
        }

        const otherMeshGeometry = otherMeshVertexData.toTransferrable();
        const featureData: JoinGeometryWorkerPayload = {
            otherMeshDynamicId: targetMeshNode._dynamicId,
            otherMeshGeometry: otherMeshGeometry.vertexData,
            otherMeshTransform: targetMesh.computeWorldMatrix(true).toArray(),
            thisMeshTransform: thisMesh.computeWorldMatrix(true).toArray(),
        };
        payload.featureData = featureData;
        payload.transfer = otherMeshGeometry.transfer;
        return payload;
    }

    updateMeshGeometry(
        feature: JoinGeometryFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>
    ) {
        const targetMesh = joinedMeshes.get(feature._dynamicId);
        const parent = feature._parent!;
        if (!targetMesh) {
            // This will just return the same vertex data
            return this.processor.updateMeshGeometry(feature, vertexData, vertexDataChanges, {
                meshDynamicId: parent._dynamicId,
            });
        }

        const thisMesh = this.viewer.getRendererNode(feature._parent!);

        if (targetMesh === thisMesh) {
            return vertexDataChanges;
        }

        const otherMeshTransform = targetMesh.computeWorldMatrix(true).clone();
        const thisMeshTransform = thisMesh.computeWorldMatrix(true);
        const otherMeshGeometry = SubmeshVertexData.ExtractFromMesh(targetMesh);

        const dependencyData: JoinGeometryDependencyData = {
            otherMeshGeometry,
            otherMeshTransform,
            thisMeshTransform,
        };
        return this.processor.updateMeshGeometry(feature, vertexData, vertexDataChanges, {
            meshDynamicId: parent._dynamicId,
            data: dependencyData,
        });
    }

    updatePreviewMesh(node: JoinGeometryFeature) {
        let featureInstance = this.targetMeshVisualizationMap.get(node.id);
        const scene = this.previewLayer.utilityLayerScene;
        const targetMesh = joinedMeshes.get(node._dynamicId);
        if (targetMesh && !featureInstance) {
            featureInstance = createJoinGeometryVisualization(targetMesh.clone(), scene, Color3.Yellow());
            featureInstance.isPickable = false;
            featureInstance.setEnabled(false);
            this.targetMeshVisualizationMap.set(node.id, featureInstance);
            return featureInstance;
        } else if (targetMesh) {
            this.deleteTargetMeshVisualization(node as ITrackedClass<JoinGeometryFeature>);
            featureInstance = createJoinGeometryVisualization(targetMesh.clone(), scene, Color3.Yellow());
            featureInstance.isPickable = false;
            featureInstance.setEnabled(false);
            this.targetMeshVisualizationMap.set(node.id, featureInstance);
            return featureInstance;
        }
    }

    update3dInternal(feature: ITrackedClass<JoinGeometryFeature>) {
        if (feature.changes.hasAny('meshId', 'enabled')) {
            const existingSubscription = this.bbSubscriptions.get(feature._dynamicId);
            if (existingSubscription) {
                existingSubscription.unsubscribe();
            }

            if (feature.enabled) {
                const targetMeshNode = feature._manager?.getById<SpaceNode>(feature.meshId);
                if (targetMeshNode && targetMeshNode instanceof MeshNode) {
                    // if this is a custom mesh node and the geometry isn't loaded, we have to load it
                    if (
                        targetMeshNode instanceof CustomMeshNode &&
                        targetMeshNode.hasGeometry &&
                        !targetMeshNode.geometryLoaded
                    ) {
                        this.viewer.rendererManager.mesh.loadNewGeometry(targetMeshNode);
                    }
                    const targetMesh = this.viewer.getRendererNode(targetMeshNode);

                    if (targetMesh instanceof Mesh && feature._parent) {
                        joinedMeshes.set(feature._dynamicId, targetMesh);

                        this.bbSubscriptions.set(
                            feature._dynamicId,
                            // TODO This should also watch the parent group for positional changes, otherwise the join won't update when the feature's parent mesh moves.
                            // But watch out for infinite loops
                            this.viewer.bbCache.watchForChanges(targetMeshNode).subscribe(() => {
                                feature.setPropertyIsModified('meshId', true);
                            })
                        );
                    } else {
                        console.warn('Could not create CSG for join geometry');
                    }
                }
            }
        }

        let featureInstance = this.targetMeshVisualizationMap.get(feature.id);
        const originalMesh = this.parentMeshVisualizationMap.get(feature.id);

        if (feature._previewVisible) {
            featureInstance = this.updatePreviewMesh(feature);
        }

        if (feature._previewVisible && feature.enabled) {
            if (feature.joinType === eJoinType.cut) {
                originalMesh?.setEnabled(false);
                featureInstance?.setEnabled(true);
            } else if (feature.joinType === eJoinType.intersect) {
                originalMesh?.setEnabled(true);
                featureInstance?.setEnabled(true);
            } else {
                originalMesh?.setEnabled(false);
                featureInstance?.setEnabled(false);
            }
        } else {
            originalMesh?.setEnabled(false);
            featureInstance?.setEnabled(false);
        }
    }

    postUpdateGeometry(feature: ITrackedClass<JoinGeometryFeature>, vertexData: SubmeshVertexData) {
        let previewMesh = this.parentMeshVisualizationMap.get(feature.id);

        if (!previewMesh) {
            const newMeshInstance = new Mesh('preview-mesh');
            newMeshInstance.doNotSerialize = true;
            vertexData.applyToMesh(newMeshInstance);
            this.parentMeshVisualizationMap.set(feature.id, newMeshInstance);
            if (newMeshInstance instanceof Mesh) {
                previewMesh = createJoinGeometryVisualization(
                    newMeshInstance,
                    this.previewLayer.utilityLayerScene,
                    Color3.Yellow()
                );
                this.parentMeshVisualizationMap.set(feature.id, previewMesh);
                previewMesh.setEnabled(false);
                if (feature._parent) {
                    const featureParentMesh = this.viewer.getRendererNode(feature._parent);
                    newMeshInstance.position.addInPlace(featureParentMesh.absolutePosition);
                }
            }
        }
    }

    delete3d(feature: ITrackedClass<JoinGeometryFeature>, preserveCache: boolean) {
        if (!preserveCache) {
            joinedMeshes.delete(feature._dynamicId);
        }
        this.bbSubscriptions.get(feature._dynamicId)?.unsubscribe();
        this.bbSubscriptions.delete(feature._dynamicId);
        this.deleteTargetMeshVisualization(feature);

        const targetMeshCopy = this.parentMeshVisualizationMap.get(feature.id);
        targetMeshCopy?.dispose(false, true);
        this.parentMeshVisualizationMap.delete(feature.id);
        super.delete3d(feature);
    }

    deleteTargetMeshVisualization(feature: ITrackedClass<JoinGeometryFeature>) {
        const previewMesh = this.targetMeshVisualizationMap.get(feature.id);
        previewMesh?.dispose(false, true);
        this.targetMeshVisualizationMap.delete(feature.id);
    }

    dispose() {
        this.bbSubscriptions.forEach(s => {
            s.unsubscribe();
        });
        this.bbSubscriptions.clear();
        joinedMeshes.clear();
        super.dispose();
    }
}
